import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Spin, Result } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import AuthLayout from '../Auth/AuthLayout';

import SetupBegin from './SetupBegin';
import SetupCompany from './SetupCompany';
import SetupServer from './SetupServer';
import SetupSmtp from './SetupSmtp';
import SetupEnd from './SetupEnd';

import useSetupConfig from './hooks/useSetupConfig';
import useSetupCompany from './hooks/useSetupCompany';
import useSetupServer from './hooks/useSetupServer';
import useSetupSmtp from './hooks/useSetupSmtp';

export default function SetupPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [cachedData, setCachedData] = useState({
    company: {},
    server: {},
    smtp: {},
  });

  const { loading, values: initialValues = {} } = useSetupConfig();

  const { pending: pendingCompany, save: saveCompany } = useSetupCompany();

  const { pending: pendingServer, save: saveServer } = useSetupServer();

  const {
    pending: pendingSmtp,
    save: saveSmtp,
    pendingCheck,
    checkEmail,
  } = useSetupSmtp();

  const handleNextStep = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const handlePrevStep = useCallback(() => {
    setStep(step - 1);
  }, [step]);

  const handleChange = useCallback(
    (changedValues, allValues) => {
      switch (step) {
        case 1:
          setCachedData((state) => ({ ...state, company: allValues }));
          break;
        case 2:
          setCachedData((state) => ({ ...state, server: allValues }));
          break;
        case 3:
          setCachedData((state) => ({ ...state, smtp: allValues }));
          break;
        default:
          break;
      }
    },
    [step]
  );

  const handleDone = useCallback(
    (values) => {
      switch (step) {
        case 1:
          saveCompany(values, () => handleNextStep());
          break;
        case 2:
          saveServer(values, () => handleNextStep());
          break;
        case 3:
          saveSmtp(values, () => handleNextStep());
          break;
        default:
          break;
      }
    },
    [step, handleNextStep, saveCompany, saveServer, saveSmtp]
  );

  const title = useMemo(() => {
    if (step === 0) {
      return t('setup-title');
    }
    if (step === 4) {
      return t('setup-end-title');
    }
  }, [step]);

  const cachedValues = useMemo(() => {
    switch (step) {
      case 1:
        return { ...initialValues.company, ...cachedData.company };
      case 2:
        return { ...initialValues.server, ...cachedData.server };
      case 3:
        return { ...initialValues.smtp, ...cachedData.smtp };
      default:
        break;
    }
  }, [step, cachedData, initialValues]);

  const content = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <SetupBegin initialValues={initialValues} onDone={handleNextStep} />
        );
      case 1:
        return (
          <SetupCompany
            initialValues={cachedValues}
            step={step - 1}
            pending={pendingCompany}
            onChange={handleChange}
            onDone={handleDone}
          />
        );
      case 2:
        return (
          <SetupServer
            initialValues={cachedValues}
            step={step - 1}
            pending={pendingServer}
            onChange={handleChange}
            onCancel={handlePrevStep}
            onDone={handleDone}
          />
        );
      case 3:
        return (
          <SetupSmtp
            initialValues={cachedValues}
            step={step - 1}
            pending={pendingSmtp}
            pendingCheck={pendingCheck}
            checkEmail={checkEmail}
            onChange={handleChange}
            onCancel={handlePrevStep}
            onDone={handleDone}
          />
        );
      case 4:
        return <SetupEnd onDone={() => history.push('/login')} />;
      default:
        return <SetupBegin />;
    }
  }, [
    step,
    initialValues,
    pendingCompany,
    pendingServer,
    pendingSmtp,
    handleDone,
    handleNextStep,
    handlePrevStep,
  ]);

  return (
    <div className="setup-page">
      <AuthLayout title={title}>
        {loading ? (
          <Spin
            spinning
            indicator={<LoadingOutlined />}
            size="large"
            className="setup-page__loader"
          />
        ) : (
          content
        )}
      </AuthLayout>
    </div>
  );
}
